// features/cx/payments/api.ts

import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  cxApiClient,
  onboardingApiClient,
  authApiClient,
} from "@shared/api/clients/apiClientFactory";
import { PaymentMethod } from "../shared/types/consumerRequest";

export const paymentsApi = {
  downloadBulkPaymentCsvTemplate: async () => {
    const url = ApiEndpoints.createUrl("cx", "payment-csv-download");
    const response = await cxApiClient.get(url, { responseType: "blob" });
    return response.data;
  },
  getCheques: async (filters: any) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "payments/cheques",
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        if (filters.search) query.push("search", filters.search);
        if (filters.bank) query.push("bank", filters.bank);
        if (filters.source) query.push("channel", filters.source); // Based on user image, source is probably channel? Wait, channel is integer 0=Counter, 1=Field Agent. We'll leave source for now or change to channel. Let's just push what's defined.
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.status) query.push("status", filters.status);
        if (filters.tab) query.push("tab", filters.tab);
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  exportCheques: async (filters: any) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "payments/cheques",
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        if (filters.search) query.push("search", filters.search);
        if (filters.bank) query.push("bank", filters.bank);
        if (filters.source) query.push("channel", filters.source);
        if (filters.tab) query.push("tab", filters.tab);
        query.push("export", "1");
        return query;
      },
    );

    const response = await cxApiClient.get(url, { responseType: "blob" });
    return response.data;
  },

  getChequesKpi: async (remoteUtilityId: number) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "payments/cheques/kpi",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId.toString());
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  getChequeDetails: async (chequeId: number, remoteUtilityId: number) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      `payments/cheques/${chequeId}`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId.toString());
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data?.result;
  },

  getBanks: async (remoteUtilityId: number) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "payments/banks",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId.toString());
        return query;
      },
    );
    const response = await cxApiClient.get(url);
    return response.data;
  },

  createBank: async (payload: any) => {
    const url = ApiEndpoints.createUrl("cx", "payments/banks");
    const response = await cxApiClient.post(url, payload);
    return response.data;
  },

  deleteBank: async (bankId: number, remoteUtilityId: number) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      `payments/banks/${bankId}`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId.toString());
        return query;
      },
    );
    const response = await cxApiClient.delete(url);
    return response.data;
  },

  markChequeDishonoured: async (chequeId: number, payload: any) => {
    const url = ApiEndpoints.createUrl(
      "cx",
      `payments/cheques/${chequeId}/mark-dishonoured`,
    );
    const response = await cxApiClient.post(url, payload);
    return response.data;
  },

  updateChequeStatus: async (
    chequeId: number,
    action: "mark-deposited" | "mark-cleared",
    payload: { remote_utility_id: number },
  ) => {
    const url = ApiEndpoints.createUrl(
      "cx",
      `payments/cheques/${chequeId}/${action}`,
    );
    const response = await cxApiClient.patch(url, payload);
    return response.data;
  },

  getUserNames: async (remoteUtilityId: number) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "auth",
      "user-name",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId.toString());
        return query;
      },
    );
    // authApiClient might not be imported in this file!
    // Wait, let me check the imports first. Let me just use authApiClient but I need to make sure it's imported.
    const response = await authApiClient.get(url);
    return response.data?.result || [];
  },

  searchConsumer: async (filters: Record<string, any>) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-search/info",
      (query) => {
        query.push("remote_utility_id", filters.remoteUtilityId.toString());
        query.push("search_data", filters.searchData);
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data?.result;
  },

  // Consumer Meta Data
  getConsumerMetaData: async (params: {
    consumerId: number;
    remoteUtilityId: number;
  }) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-meta-data",
      (query) => {
        query.push("consumer_id", params.consumerId.toString());
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  // Payment Management Summary
  getPaymentManagementSummary: async (remoteUtilityId: number) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "payment-management/summary",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId.toString());
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  // Credit Note Summary
  getCreditNoteSummary: async (remoteUtilityId: number) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "credit-note/summary",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId.toString());
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  // Latest Consumer Bill
  getPendingConsumerBill: async (params: {
    remoteUtilityId: number;
    consumerId: number;
  }) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "pending-consumer-bills",
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        query.push("consumer_id", params.consumerId.toString());
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  // Payment Services
  getPaymentServices: async (params: {
    remoteUtilityId: number;
    consumerId: number;
  }) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "payment-services",
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        query.push("consumer_id", params.consumerId.toString());
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  // Create Payment
  createPayment: async (payload: any) => {
    const url = ApiEndpoints.createUrl("cx", "payment");
    const response = await cxApiClient.post(url, payload);
    return response.data;
  },

  createPlanPayment: async (payload: any) => {
    const url = ApiEndpoints.createUrl("cx", "payment");
    const response = await cxApiClient.post(url, payload);
    return response.data;
  },

  // Create Credit Note
  createCreditNote: async (payload: any) => {
    const url = ApiEndpoints.createUrl("cx", "credit-note");
    const response = await cxApiClient.post(url, payload);
    return response.data;
  },

  getServiceOrder: async (remoteUtilityId: string, consumerId: string) => {
    console.log("consumerId", consumerId);
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "payment-service-order",
      (qs) => {
        qs.push("remote_utility_id", remoteUtilityId);
        qs.push("consumer_id", consumerId);
      },
    );

    const response = await cxApiClient.get(url);
    return response.data?.result;
  },

  // Get Credit Notes List
  getCreditNotes: async (filters: any) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "credit-note",
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.searchData) query.push("search_data", filters.searchData);
        if (filters.paymentReceivedStatus)
          query.push("payment_received_status", filters.paymentReceivedStatus);
        if (filters.paymentPayType?.length) {
          filters.paymentPayType.forEach((type: any) =>
            query.push("payment_pay_type", type),
          );
        }
        if (filters.startDate) query.push("start_date", filters.startDate);
        if (filters.endDate) query.push("end_date", filters.endDate);
        if (filters.paymentMode)
          query.push("payment_mode", filters.paymentMode);
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  exportCreditNotesList: async (filters: any) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "credit-note",
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.searchData) query.push("search_data", filters.searchData);
        if (filters.paymentReceivedStatus)
          query.push("payment_received_status", filters.paymentReceivedStatus);
        if (filters.paymentPayType?.length) {
          filters.paymentPayType.forEach((type: any) =>
            query.push("payment_pay_type", type),
          );
        }
        if (filters.startDate) query.push("start_date", filters.startDate);
        if (filters.endDate) query.push("end_date", filters.endDate);
        if (filters.paymentMode)
          query.push("payment_mode", filters.paymentMode);
        query.push("export_format", "csv");
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  getCreditNoteDetail: async (
    creditNoteId: number,
    remoteUtilityId: string,
  ) => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "credit-note",
      ["detail", creditNoteId],
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data?.result;
  },

  getPaymentDetail: async (params: {
    paymentId: number;
    remoteUtilityId: string;
  }) => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "payment-management",
      ["detail", params.paymentId.toString()],
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId);
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data?.result;
  },

  getPaymentSingleBillDetail: async (params: {
    paymentId: number;
    remoteUtilityId: number;
  }) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      `payments/${params.paymentId}/detail`,
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        return query;
      },
    );
    const response = await cxApiClient.get(url);
    return response.data?.result;
  },

  getPaymentBillAllocations: async (params: {
    paymentId: number;
    remoteUtilityId: number;
  }) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      `payments/${params.paymentId}/bill-allocations`,
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        return query;
      },
    );
    const response = await cxApiClient.get(url);
    return response.data?.result || response.data;
  },

  // Choice APIs
  getChoices: async (choice: string) => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "payments/choices",
      [choice],
      (query) => query,
    );
    const response = await cxApiClient.get(url);
    return response.data?.result || response.data;
  },

  getCreditNoteReasons: async () => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "choices",
      ["credit_note_reason"],
      (query) => query,
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  getPaymentReceivedStatusChoices: async (remoteUtilityId: number) => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "choices",
      ["payment_received_status"],
      (query) => {
        query.push("remote_utility_id", remoteUtilityId.toString());
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  getPaymentChannelChoices: async (remoteUtilityId: number) => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "onboarding",
      "config-map",
      ["payment_channel"],
      (query) => {
        query.push("remote_utility_id", remoteUtilityId.toString());
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  getPaymentPayTypeChoices: async (remoteUtilityId: number) => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "choices",
      ["payment_pay_type"],
      (query) => {
        query.push("remote_utility_id", remoteUtilityId.toString());
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  getPayments: async (filters: any) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "credit-note",
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.searchData) query.push("search_data", filters.searchData);
        if (filters.paymentReceivedStatus)
          query.push("payment_received_status", filters.paymentReceivedStatus);
        if (filters.paymentPayType?.length) {
          filters.paymentPayType.forEach((type: any) =>
            query.push("payment_pay_type", type),
          );
        }
        if (filters.startDate) query.push("start_date", filters.startDate);
        if (filters.endDate) query.push("end_date", filters.endDate);
        if (filters.paymentMode)
          query.push("payment_mode", filters.paymentMode);
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  exportPaymentsList: async (filters: any) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "credit-note",
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.searchData) query.push("search_data", filters.searchData);
        if (filters.paymentReceivedStatus)
          query.push("payment_received_status", filters.paymentReceivedStatus);
        if (filters.paymentPayType?.length) {
          filters.paymentPayType.forEach((type: any) =>
            query.push("payment_pay_type", type),
          );
        }
        if (filters.startDate) query.push("start_date", filters.startDate);
        if (filters.endDate) query.push("end_date", filters.endDate);
        if (filters.paymentMode)
          query.push("payment_mode", filters.paymentMode);
        query.push("export_format", "csv");
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  updatePayment: async (paymentId: number, payload: any) => {
    const url = ApiEndpoints.createUrlWithPathVariables("cx", "payment", [
      paymentId.toString(),
    ]);
    const response = await cxApiClient.put(url, payload);
    return response.data;
  },

  deletePayment: async (paymentId: number) => {
    const url = ApiEndpoints.createUrlWithPathVariables("cx", "payment", [
      paymentId.toString(),
    ]);
    await cxApiClient.delete(url);
  },

  getInstallments: async (
    remoteUtilityId: number,
    consumerId: number,
    consumerNo: string,
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "payment-installment",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("consumer_id", consumerId);
        query.push("consumer_no", consumerNo);
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data?.result;
  },

  getPaymentMode: async (remoteUtilityId: string) => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "onboarding",
      "config-map",
      ["payment_mode"],
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        return query;
      },
    );

    const response = await onboardingApiClient.get(url);
    return response.data?.result;
  },

  getBulkPaymentBatches: async (filters: {
    remoteUtilityId: number;
    page?: number;
    pageSize?: number;
  }) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "payments/bulk-payment/batch",
      (query) => {
        if (filters.remoteUtilityId) {
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        }
        if (filters.page) {
          query.push("page", filters.page.toString());
        }
        if (filters.pageSize) {
          query.push("page_size", filters.pageSize.toString());
        }
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  uploadBulkPaymentBatch: async (payload: FormData) => {
    const url = ApiEndpoints.createUrl(
      "cx",
      "payments/bulk-payment/batch/upload",
    );
    const response = await cxApiClient.post(url, payload);
    return response.data;
  },

  getBulkPaymentBatchPreview: async (id: number) => {
    const url = ApiEndpoints.createUrl(
      "cx",
      `payments/bulk-payment/batch/${id}/preview`,
    );
    const response = await cxApiClient.get(url);
    return response.data?.result;
  },

  saveBulkPaymentBatchMappings: async (
    id: number,
    payload: { column_mappings: Record<string, string> },
  ) => {
    const url = ApiEndpoints.createUrl(
      "cx",
      `payments/bulk-payment/batch/${id}/start-processing`,
    );
    const response = await cxApiClient.post(url, payload);
    return response.data;
  },

  getBulkPaymentBatchDetails: async (id: number) => {
    const url = ApiEndpoints.createUrl(
      "cx",
      `payments/bulk-payment/batch/${id}`,
    );
    const response = await cxApiClient.get(url);
    return response.data?.result;
  },

  getBulkPaymentBatchRecords: async (filters: {
    id: number;
    status?: string;
    search?: string;
    page?: number;
    pageSize?: number;
  }) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      `payments/bulk-payment/batch/${filters.id}/records`,
      (query) => {
        if (filters.status) query.push("status", filters.status);
        if (filters.search) query.push("search", filters.search);
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.pageSize)
          query.push("page_size", filters.pageSize.toString());
        return query;
      },
    );
    const response = await cxApiClient.get(url);
    return response.data;
  },

  postMatchedBulkPaymentBatch: async (id: number) => {
    const url = ApiEndpoints.createUrl(
      "cx",
      `payments/bulk-payment/batch/${id}/post-matched`,
    );
    const response = await cxApiClient.post(url, {});
    return response.data;
  },

  exportExceptionsBulkPaymentBatch: async (id: number) => {
    const url = ApiEndpoints.createUrl(
      "cx",
      `payments/bulk-payment/batch/${id}/export-exceptions`,
    );
    const response = await cxApiClient.get(url, { responseType: "blob" });
    return response.data;
  },

  searchBulkPaymentConsumers: async (filters: {
    search: string;
    remoteUtilityId: number;
  }) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "payments/bulk-payment/consumer-search",
      (query) => {
        query.push("search", filters.search);
        query.push("remote_utility_id", filters.remoteUtilityId.toString());
        return query;
      },
    );
    const response = await cxApiClient.get(url);
    return response.data?.result;
  },
  searchBulkPaymentConsumersForBillData: async (filters: {
    search: string;
    remoteUtilityId: number;
  }) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "payments/bulk-payment/consumer-search",
      (query) => {
        query.push("search", filters.search);
        query.push("remote_utility_id", filters.remoteUtilityId.toString());
        query.push("is_bill_data", "1");
        return query;
      },
    );
    const response = await cxApiClient.get(url);
    return response.data?.result;
  },

  resolveBulkPaymentRecord: async (
    id: number,
    payload: { consumer_id: number },
  ) => {
    const url = ApiEndpoints.createUrl(
      "cx",
      `payments/bulk-payment/record/${id}/resolve`,
    );
    const response = await cxApiClient.post(url, payload);
    return response.data;
  },

  getDayEndReconciliations: async (filters: any) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "payments/reconciliation",
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        if (filters.search) query.push("search", filters.search);
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  startReconciliation: async (payload: any) => {
    const url = ApiEndpoints.createUrl("cx", "payments/reconciliation/start");
    const response = await cxApiClient.post(url, payload);
    return response.data;
  },

  getSlipLots: async (filters: any) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "payments/slip-lot",
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        if (filters.search) query.push("search", filters.search);
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.centre) query.push("centre", filters.centre);
        if (filters.status) query.push("status", filters.status);
        if (filters.dateRange) query.push("date_range", filters.dateRange);
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  createSlipLot: async (payload: any) => {
    const url = ApiEndpoints.createUrl("cx", "payments/slip-lot");
    const response = await cxApiClient.post(url, payload);
    return response.data;
  },

  getSlipLotById: async (id: number | string) => {
    const url = ApiEndpoints.createUrl("cx", `payments/slip-lot/${id}`);
    const response = await cxApiClient.get(url);
    return response.data;
  },

  getPaymentMethod: async (params: {
    remoteUtilityId: number;
  }): Promise<{ result: any }> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "onboarding",
      "config-map",
      ["payment_mode"],
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        return query;
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  addSlipLotEntry: async (lotId: string | number, payload: any) => {
    const url = ApiEndpoints.createUrlWithPathVariables("cx", "payments", [
      "slip-lot",
      lotId.toString(),
      "entry",
    ]);
    const response = await cxApiClient.post(url, payload);
    return response.data;
  },

  getSlipLotEntries: async (filters: any) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      `payments/slip-lot/${filters.lotId}/entry`,
      (query) => {
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        return query;
      },
    );
    const response = await cxApiClient.get(url);
    return response.data;
  },

  updateSlipLotEntry: async (entryId: number | string, payload: any) => {
    const url = ApiEndpoints.createUrlWithPathVariables("cx", "payments", [
      "slip-lot",
      "entry",
      entryId.toString(),
    ]);
    const response = await cxApiClient.put(url, payload);
    return response.data;
  },

  deleteSlipLotEntry: async (entryId: number | string) => {
    const url = ApiEndpoints.createUrlWithPathVariables("cx", "payments", [
      "slip-lot",
      "entry",
      entryId.toString(),
    ]);
    const response = await cxApiClient.delete(url);
    return response.data;
  },

  submitSlipLot: async (lotId: number | string) => {
    const url = ApiEndpoints.createUrlWithPathVariables("cx", "payments", [
      "slip-lot",
      lotId.toString(),
      "submit",
    ]);
    const response = await cxApiClient.post(url, {});
    return response.data;
  },

  scanSlipLot: async (lotId: number | string, payload: { payload: string }) => {
    const url = ApiEndpoints.createUrlWithPathVariables("cx", "payments", [
      "slip-lot",
      lotId.toString(),
      "scan",
    ]);
    const response = await cxApiClient.post(url, payload);
    return response.data;
  },

  getSlipLotRecentScans: async (lotId: number | string) => {
    const url = ApiEndpoints.createUrlWithPathVariables("cx", "payments", [
      "slip-lot",
      lotId.toString(),
      "recent-scans",
    ]);
    const response = await cxApiClient.get(url);
    return response.data;
  },

  exportSlipLotErrors: async (lotId: number | string) => {
    const url = ApiEndpoints.createUrlWithPathVariables("cx", "payments", [
      "slip-lot",
      lotId.toString(),
      "export-errors",
    ]);
    const response = await cxApiClient.get(url, { responseType: "blob" });
    return response.data;
  },

  getOutstandingBills: async (filters: any) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "outstanding-bills",
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        if (filters.searchData) query.push("search_data", filters.searchData);
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  getReconciliationSummary: async (id: number | string) => {
    const url = ApiEndpoints.createUrlWithPathVariables("cx", "payments", [
      "reconciliation",
      "summary",
      id.toString(),
    ]);
    const response = await cxApiClient.get(url);
    return response.data?.result || response.data;
  },

  getReconciliationDrillDown: async (
    id: number | string,
    channelId: number | string,
    page?: number,
    limit?: number,
    search?: string,
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      `payments/reconciliation/drill-down/${id}`,
      (query) => {
        if (channelId) query.push("payment_mode", channelId.toString());
        if (page) query.push("page", page.toString());
        if (limit) query.push("limit", limit.toString());
        if (search) query.push("search", search);
        return query;
      },
    );
    const response = await cxApiClient.get(url);
    return response.data?.result || response.data;
  },

  getReconciliationById: async (
    id: number | string,
    remoteUtilityId: number | string,
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      `payments/reconciliation/${id}`,
      (query) => {
        if (remoteUtilityId)
          query.push("remote_utility_id", remoteUtilityId.toString());
        return query;
      },
    );
    const response = await cxApiClient.get(url);
    return response.data?.result || response.data;
  },

  getActivityLogs: async (filters: {
    remoteUtilityId: number | string;
    objectType?: string;
    module?: string;
    limit?: number;
    requestId?: string;
  }) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "payments/activity-log",
      (query) => {
        if (filters.remoteUtilityId) {
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        }
        if (filters.objectType) {
          query.push("object_type", filters.objectType);
        }
        if (filters.module) {
          query.push("module", filters.module);
        }
        if (filters.limit) {
          query.push("limit", filters.limit.toString());
        }
        if (filters.requestId) {
          query.push("request_id", filters.requestId);
        }
        return query;
      },
    );
    const response = await cxApiClient.get(url);
    return response.data;
  },

  exportReconciliationDrillDown: async (
    id: number | string,
    channelId: number | string,
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      `payments/reconciliation/drill-down/export/${id}`,
      (query) => {
        if (channelId) query.push("channel", channelId.toString());
        return query;
      },
    );
    const response = await cxApiClient.get(url, { responseType: "blob" });
    return response.data;
  },

  exportReconciliation: async (id: number | string) => {
    const url = ApiEndpoints.createUrl(
      "cx",
      `payments/reconciliation/export/${id}`,
    );
    const response = await cxApiClient.get(url, { responseType: "blob" });
    return response.data;
  },

  getReconciliationSystemReceipts: async (filters: {
    remoteUtilityId: number | string;
    periodFrom?: string;
    periodTo?: string;
    page?: number;
    limit?: number;
    search?: string;
  }) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "payments/reconciliation/step2/system-receipts",
      (query) => {
        if (filters.remoteUtilityId) {
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        }
        if (filters.periodFrom) {
          query.push("period_from", filters.periodFrom);
        }
        if (filters.periodTo) {
          query.push("period_to", filters.periodTo);
        }
        if (filters.page) {
          query.push("page", filters.page.toString());
        }
        if (filters.limit) {
          query.push("limit", filters.limit.toString());
        }
        if (filters.search) {
          query.push("search", filters.search);
        }
        return query;
      },
    );
    const response = await cxApiClient.get(url);
    return response.data;
  },

  signOffReconciliation: async (
    id: number | string,
    payload: { manager_name: string; sign_off_method: number; remarks: string },
  ) => {
    const url = ApiEndpoints.createUrl(
      "cx",
      `payments/reconciliation/sign-off/${id}`,
    );
    const response = await cxApiClient.post(url, payload);
    return response.data;
  },

  uploadReconciliationStatement: async (params: {
    file: File;
    remoteUtilityId: number | string;
    bankAccountId: number | string;
    onUploadProgress?: (progressEvent: any) => void;
  }) => {
    const url = ApiEndpoints.createUrl(
      "cx",
      "payments/reconciliation/step2/upload",
    );
    const formData = new FormData();
    formData.append("file", params.file);
    formData.append("remote_utility_id", params.remoteUtilityId.toString());
    formData.append("bank_account_id", params.bankAccountId.toString());

    const response = await cxApiClient.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: params.onUploadProgress,
    });
    return response.data;
  },

  checkTransferPayment: async (paymentId: number, remoteUtilityId: number) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      `payments/transfer/${paymentId}/check`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId.toString());
        return query;
      },
    );
    const response = await cxApiClient.get(url);
    return response.data?.result;
  },

  transferConsumerSearch: async (filters: {
    remoteUtilityId: number;
    search: string;
    excludeConsumerId: number;
  }) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "payments/transfer/consumer-search",
      (query) => {
        query.push("remote_utility_id", filters.remoteUtilityId.toString());
        if (filters.search) query.push("search", filters.search);
        if (filters.excludeConsumerId) {
          query.push(
            "exclude_consumer_id",
            filters.excludeConsumerId.toString(),
          );
        }
        return query;
      },
    );
    const response = await cxApiClient.get(url);
    return response.data?.result || [];
  },

  transferPayment: async (
    paymentId: number,
    payload: {
      destination_consumer_id: number;
      remote_utility_id: number;
      transfer_reason?: string;
    },
  ) => {
    const url = ApiEndpoints.createUrl("cx", `payments/transfer/${paymentId}`);
    const response = await cxApiClient.post(url, payload);
    return response.data;
  },

  getPaymentChoices: async (choiceType: string) => {
    const url = ApiEndpoints.createUrl("cx", `payments/choices/${choiceType}`);
    const response = await cxApiClient.get(url);
    return response.data;
  },

  checkRefundPayment: async (paymentId: number, remoteUtilityId: number) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      `payments/refund/${paymentId}/check`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId.toString());
        return query;
      },
    );
    const response = await cxApiClient.get(url);
    return response.data?.result;
  },

  reviewRefundPayment: async (paymentId: number, payload: any) => {
    const url = ApiEndpoints.createUrl(
      "cx",
      `payments/refund/${paymentId}/review`,
    );
    const response = await cxApiClient.post(url, payload);
    return response.data;
  },
};
